import { Spin } from 'antd'
import {
  LABEL_WIDTH,
  SectionContainer,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { FfsSharedValuesContext } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import _ from 'lodash'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { YieldStrengthParameterField } from './allowableStress/MetalAllowableStressTable'

interface FfsMawpSectionProps {
  mawp: GeckoMeasurement | null
  mawpr: GeckoMeasurement | null

  // Editables
  qualityFactor: GeckoMeasurement | null
  setQualityFactor?: Dispatch<SetStateAction<GeckoMeasurement | null>>
  pressureCoefficient: GeckoMeasurement | null
  setPressureCoefficient: Dispatch<SetStateAction<GeckoMeasurement | null>>
  loading: boolean
}

const TOOLTIP_MAWP = (
  <>
    (2 * t<sub>mm</sub> * S * E) / (D - 2 * t<sub>mm</sub> * Y)
  </>
)

export const LABEL_MAWP_R = (
  <>
    MAWP<sub>r</sub>
  </>
)

const WORKING_UNIT_PRESS = GeckoMeasurementUnit.Megapascals

interface HookProps {
  tc: GeckoMeasurement | null
  tmm: GeckoMeasurement | null
  outerDiameter: GeckoMeasurement | null
  rsfa: GeckoMeasurement | null
  rsf: GeckoMeasurement | null
}

export function useFfsMawpProps({
  tc,
  tmm,
  outerDiameter,
  rsfa,
  rsf,
}: HookProps): Omit<FfsMawpSectionProps, 'loading'> {
  // Quality factor, E
  const [qualityFactor, setQualityFactor] = useState<GeckoMeasurement | null>(
    GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Ratio)
  )

  // Coefficient, Y
  const [pressureCoefficient, setPressureCoefficient] =
    useState<GeckoMeasurement | null>(
      GeckoMeasurement.CreateFromValue(0.4, GeckoMeasurementUnit.Ratio)
    )

  const { yieldStrength } = useContext(FfsSharedValuesContext)
  const mawp = useMemo<GeckoMeasurement | null>(() => {
    if (
      _.isNil(tmm) ||
      _.isNil(outerDiameter) ||
      _.isNil(qualityFactor) ||
      _.isNil(pressureCoefficient) ||
      _.isNil(yieldStrength)
    ) {
      return null
    }
    return computeMawp({
      t: tmm,
      yieldStrength,
      qualityFactor,
      outerDiameter,
      pressureCoefficient,
    })
  }, [pressureCoefficient, outerDiameter, qualityFactor, tmm, yieldStrength])

  const mawpr = useMemo(() => {
    if (
      _.isNil(tc) ||
      _.isNil(outerDiameter) ||
      _.isNil(qualityFactor) ||
      _.isNil(pressureCoefficient) ||
      _.isNil(yieldStrength) ||
      _.isNil(rsf) ||
      _.isNil(rsfa)
    ) {
      return null
    }
    const tcMawp = computeMawp({
      t: tc,
      yieldStrength,
      qualityFactor,
      outerDiameter,
      pressureCoefficient,
    })
    return GeckoMeasurement.CreateFromValue(
      tcMawp.toNumber(WORKING_UNIT_PRESS) *
        (rsf.toNumber(GeckoMeasurementUnit.Ratio) /
          rsfa.toNumber(GeckoMeasurementUnit.Ratio)),
      WORKING_UNIT_PRESS
    )
  }, [
    outerDiameter,
    pressureCoefficient,
    qualityFactor,
    rsf,
    rsfa,
    tc,
    yieldStrength,
  ])

  return useMemo(
    () => ({
      rsf,
      qualityFactor,
      setQualityFactor,
      pressureCoefficient,
      setPressureCoefficient,
      mawp,
      mawpr,
    }),
    [mawp, mawpr, pressureCoefficient, qualityFactor, rsf]
  )
}

type ComputeMawpProps = {
  t: GeckoMeasurement
  yieldStrength: GeckoMeasurement
  qualityFactor: GeckoMeasurement
  outerDiameter: GeckoMeasurement
  pressureCoefficient: GeckoMeasurement
}
function computeMawp({
  t,
  yieldStrength,
  qualityFactor,
  outerDiameter,
  pressureCoefficient,
}: ComputeMawpProps): GeckoMeasurement {
  const tNum = t.toNumber(GeckoMeasurementUnit.Inches)
  const sNum = yieldStrength.toNumber(GeckoMeasurementUnit.PoundsForcePerSqInch)
  const eNum = qualityFactor.toNumber(GeckoMeasurementUnit.Ratio)
  const dNum = outerDiameter.toNumber(GeckoMeasurementUnit.Inches)
  const yNum = pressureCoefficient.toNumber(GeckoMeasurementUnit.Ratio)

  const mawpNum = (2 * tNum * sNum * eNum) / (dNum - 2 * tNum * yNum)
  return GeckoMeasurement.CreateFromValue(
    mawpNum,
    GeckoMeasurementUnit.PoundsForcePerSqInch
  )
}

const RawFfsMawpSection: FC<FfsMawpSectionProps> = ({
  loading,
  qualityFactor,
  setQualityFactor,
  pressureCoefficient,
  setPressureCoefficient,
  mawp,
  mawpr,
}) => {
  const {
    jointEfficiency,
    setJointEfficiency,
    yieldStrength,
    setYieldStrength,
  } = useContext(FfsSharedValuesContext)
  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <FfsParameterField
          label='E'
          labelTooltip='Quality Factor'
          value={qualityFactor}
          setValue={setQualityFactor}
          min={GeckoMeasurement.CreateFromValue(
            0.1,
            GeckoMeasurementUnit.Ratio
          )}
          max={GeckoMeasurement.CreateFromValue(2, GeckoMeasurementUnit.Ratio)}
          step={0.1}
          measurementType='ratio'
        />
        <FfsParameterField
          label='Y'
          labelTooltip='Pressure coefficient'
          value={pressureCoefficient}
          setValue={setPressureCoefficient}
          min={GeckoMeasurement.CreateFromValue(
            0.1,
            GeckoMeasurementUnit.Ratio
          )}
          max={GeckoMeasurement.CreateFromValue(2, GeckoMeasurementUnit.Ratio)}
          step={0.1}
          measurementType='ratio'
        />
        <FfsParameterField
          label='W'
          labelTooltip='Weld join strength reduction factor'
          value={jointEfficiency}
          setValue={setJointEfficiency}
          min={GeckoMeasurement.CreateFromValue(
            0.1,
            GeckoMeasurementUnit.Ratio
          )}
          max={GeckoMeasurement.CreateFromValue(2, GeckoMeasurementUnit.Ratio)}
          step={0.1}
          measurementType='ratio'
        />
        <YieldStrengthParameterField
          stressValue={yieldStrength}
          setStressValue={setYieldStrength}
          labelWidth={LABEL_WIDTH}
        />
        <FfsParameterField
          label='MAWP'
          labelTooltip={TOOLTIP_MAWP}
          value={mawp}
          measurementType='pressure'
        />
        {!!mawpr && (
          <FfsParameterField
            label={LABEL_MAWP_R}
            value={mawpr}
            measurementType='pressure'
          />
        )}
      </SectionContainer>
    </Spin>
  )
}

RawFfsMawpSection.displayName = 'FfsMawpSection'
export const FfsMawpSection = React.memo(RawFfsMawpSection)
